import React, { useEffect, useEffectEvent, useState, useRef } from 'react';

function App({ clientToken }) {
  const [cancelado, setCancelado] = useState(false);
  const [data, setData] = useState(null);
  const [esTuTurno, setEsTuTurno] = useState(false);
  const [turnoCaducado, setTurnoCaducado] = useState(false);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  // Modo resto: cantidad de personas y nombre del grupo antes de anotarse
  const [personasSeleccionadas, setPersonasSeleccionadas] = useState(null);
  const [nombreGrupo, setNombreGrupo] = useState('');

  // 🔥 LOCK REAL (NO async)
  const creandoTurnoRef = useRef(false);
  const tuTurnoRef = useRef(null);
  const audioContextRef = useRef(null);
  const inicialTurnoSolicitadoRef = useRef(false);
  const audioElementRef = useRef(null);
  // El aviso de "tu turno se acerca" suena/vibra una sola vez
  const avisoCercaRef = useRef(false);

  // 3 vibraciones largas. iOS web no expone la API de vibracion (solo Android);
  // en iPhone el aviso es sonoro (y vibra via push del sistema con la PWA).
  const vibrar = () => {
    if (navigator.vibrate) {
      navigator.vibrate([600, 300, 600, 300, 600]);
    }
  };

  // 2 vibraciones cortas para el aviso de "tu turno se acerca"
  const vibrarSuave = () => {
    if (navigator.vibrate) {
      navigator.vibrate([300, 200, 300]);
    }
  };

  // Generar beep con Web Audio API (Desktop/Chrome)
  const beepWebAudio = () => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return false;

    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioCtx();
      }

      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }

      const notes = [880, 1174, 880, 1174];
      notes.forEach((freq, index) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sine';
        osc.frequency.value = freq;

        osc.connect(gain);
        gain.connect(ctx.destination);

        const start = ctx.currentTime + index * 0.25;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.4, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.18);

        osc.start(start);
        osc.stop(start + 0.2);
      });
      return true;
    } catch (e) {
      return false;
    }
  };

  // Beep para iOS usando notificación de alerta (fallback)
  const beepFallback = () => {
    try {
      // Crear sonido más simple y fuerte para iOS
      const ac = new (window.AudioContext || window.webkitAudioContext)();
      
      // Resumir contexto si está suspendido
      if (ac.state === 'suspended') {
        ac.resume().catch(() => {});
      }

      const osc = ac.createOscillator();
      const gain = ac.createGain();
      
      osc.connect(gain);
      gain.connect(ac.destination);
      
      osc.frequency.value = 900;
      gain.gain.setValueAtTime(0.5, ac.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ac.currentTime + 0.2);
      
      osc.start(ac.currentTime);
      osc.stop(ac.currentTime + 0.2);
      
      // Segundo beep
      setTimeout(() => {
        try {
          const osc2 = ac.createOscillator();
          const gain2 = ac.createGain();
          osc2.connect(gain2);
          gain2.connect(ac.destination);
          osc2.frequency.value = 1200;
          gain2.gain.setValueAtTime(0.5, ac.currentTime);
          gain2.gain.exponentialRampToValueAtTime(0.01, ac.currentTime + 0.2);
          osc2.start(ac.currentTime);
          osc2.stop(ac.currentTime + 0.2);
        } catch (e) {}
      }, 250);
    } catch (e) {
      // Último recurso: Notification API con sonido (iOS)
      try {
        if (window.Notification && Notification.permission === 'granted') {
          new Notification('¡ES TU TURNO!', {
            tag: 'turno-alert',
            renotify: true,
            vibrate: [500, 200, 500],
            requireInteraction: true
          });
        }
      } catch (e2) {}
    }
  };

  const beep = () => {
    // Intentar Web Audio primero
    if (!beepWebAudio()) {
      // Fallback para iOS
      beepFallback();
    }
  };

  // Sonido de alerta: archivo mp3 (mas fuerte y reconocible), fallback a beep sintetizado
  const sonarUnaVez = () => {
    const audio = audioElementRef.current;
    if (audio) {
      try {
        audio.currentTime = 0;
        audio.play().catch(() => beep());
        return;
      } catch (e) {}
    }
    beep();
  };

  const sonar = (veces = 3) => {
    sonarUnaVez();
    for (let i = 1; i < veces; i++) {
      setTimeout(sonarUnaVez, i * 1800);
    }
  };

  const notificarTurno = useEffectEvent(() => {
    setEsTuTurno(true);
    try {
      if (window.Notification && Notification.permission === 'granted') {
        const num = tuTurnoRef.current;
        const opciones = {
          body: `Turno ${num || ''}`,
          tag: 'miturno-turno',
          renotify: true,
          vibrate: [600, 300, 600, 300, 600],
          requireInteraction: true
        };
        // Via service worker permite vibracion/persistencia tambien con pantalla bloqueada
        if (navigator.serviceWorker) {
          navigator.serviceWorker.ready
            .then(reg => reg.showNotification('🎉 ES TU TURNO', opciones))
            .catch(() => {
              try { new Notification('🎉 ES TU TURNO', opciones); } catch (e) {}
            });
        } else {
          new Notification('🎉 ES TU TURNO', opciones);
        }
      }
    } catch (e) {}

    sonar();
    vibrar();
  });

  // Aviso de proximidad (faltan pocos turnos): suena y vibra suave, una sola vez
  const avisarProximidad = useEffectEvent(() => {
    if (avisoCercaRef.current || esTuTurno) return;
    avisoCercaRef.current = true;
    sonar(1);
    vibrarSuave();
  });

  // Detectar plataforma y capacidades
  const isIOSDevice = () => {
    const ua = navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };

  // Web Push estandar (VAPID): en iOS requiere la app agregada a la pantalla
  // de inicio (iOS 16.4+); se detecta por capacidades, no por user agent.
  const supportsWebPush = () => {
    return 'serviceWorker' in navigator && 'PushManager' in window;
  };

  const isStandalone = () => {
    return window.navigator.standalone === true ||
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
  };

  const isNotificationPermissionGranted = () => {
    return typeof Notification !== 'undefined' && Notification.permission === 'granted';
  };

  // Suscribe este navegador al Web Push del comercio y lo asocia a la sesion
  // actual en el backend (asi el push llega SOLO cuando es SU turno).
  const suscribirPush = useEffectEvent(() => {
    if (!supportsWebPush() || !clientToken) return;

    const vapidKey = data?.vapid_public_key;
    if (!vapidKey) return;

    const urlBase64ToUint8Array = (base64String) => {
      const padding = '='.repeat((4 - base64String.length % 4) % 4);
      const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
      const rawData = window.atob(base64);
      const outputArray = new Uint8Array(rawData.length);
      for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
      }
      return outputArray;
    };

    navigator.serviceWorker.register('/sw.js')
      .then(() => navigator.serviceWorker.ready)
      .then(reg => reg.pushManager.getSubscription().then(existing => {
        if (existing) return existing;
        return reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey)
        });
      }))
      .then(sub => {
        if (!sub) return;
        // Siempre re-postear: actualiza el vinculo suscripcion <-> sesion
        return fetch(`/api/locales/${clientToken}/push/subscribe/`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(sub)
        });
      })
      .catch(() => {});
  });

  const askNotificationPermission = () => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          suscribirPush();
        }
        setShowNotificationPrompt(false);
      }).catch(() => {
        setShowNotificationPrompt(false);
      });
    } else {
      if (isNotificationPermissionGranted()) {
        suscribirPush();
      }
      setShowNotificationPrompt(false);
    }
  };

  useEffect(() => {
    if (
      data?.tu_turno &&
      !esTuTurno &&
      !showNotificationPrompt &&
      !isNotificationPermissionGranted() &&
      typeof Notification !== 'undefined' &&
      Notification.permission !== 'denied'
    ) {
      setShowNotificationPrompt(true);
    }
  }, [data?.tu_turno, esTuTurno, showNotificationPrompt]);

  // Banner iOS: sin instalar como PWA, Apple no permite push con Safari cerrado.
  // No existe API para automatizar "Agregar a pantalla de inicio" en iOS:
  // lo maximo posible es guiar al usuario paso a paso.
  const [iosBannerDismissed, setIosBannerDismissed] = useState(false);
  const mostrarBannerIOS = isIOSDevice() && !isStandalone() && !iosBannerDismissed && Boolean(data?.tu_turno) && !esTuTurno;

  // En iPhone el boton compartir esta en distinto lugar segun el navegador
  const getIOSBrowser = () => {
    const ua = navigator.userAgent;
    if (/CriOS/.test(ua)) return 'chrome';
    if (/FxiOS|EdgiOS/.test(ua)) return 'otro';
    return 'safari';
  };

  const IconoCompartir = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ verticalAlign: 'text-bottom', margin: '0 2px' }}>
      <path d="M12 3v12" />
      <path d="M8 7l4-4 4 4" />
      <path d="M5 11v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8" />
    </svg>
  );

  // Intenta desbloquear audio en la primera interaccion del usuario.
  useEffect(() => {
    const unlockAudio = () => {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;

      if (!audioContextRef.current) {
        audioContextRef.current = new AudioCtx();
      }

      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().catch(() => {});
      }

      // Precargar archivo de audio
      if (!audioElementRef.current) {
        audioElementRef.current = new Audio('/alert.mp3');
        audioElementRef.current.preload = 'auto';
      }
    };

    window.addEventListener('click', unlockAudio, { once: true });
    window.addEventListener('touchstart', unlockAudio, { once: true });

    return () => {
      window.removeEventListener('click', unlockAudio);
      window.removeEventListener('touchstart', unlockAudio);
    };
  }, []);

  // Si ya hay permiso de notificaciones, (re)suscribir apenas tengamos la clave VAPID.
  useEffect(() => {
    if (clientToken && data?.vapid_public_key && isNotificationPermissionGranted()) {
      suscribirPush();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientToken, data?.vapid_public_key]);

  // 🔄 polling estado
  useEffect(() => {
    if (!clientToken) {
      setData({ error: 'Link de cliente invalido.' });
      return undefined;
    }

    if (cancelado) return;

    const apiBase = `/api/locales/${clientToken}`;

    const fetchData = () => {
      fetch(`${apiBase}/estado/`, { credentials: 'include' })
        .then(res => res.json())
        .then(json => {

          if (json.comercio_inactivo) {
            setData(json);
            return;
          }

          if (json.error) {
            setData({ error: json.error, nombre_local: json.nombre_local, sucursal: json.sucursal });
            return;
          }

          if (json.cerrado) {
            setData({ cerrado: true, mensaje: json.mensaje });
            return;
          }

          if (json?.tu_turno) {
            tuTurnoRef.current = json.tu_turno;
            setTurnoCaducado(false);
            if (!isNotificationPermissionGranted() && typeof Notification !== 'undefined' && Notification.permission !== 'denied') {
              setShowNotificationPrompt(true);
            }
          }

          if (json?.estado === 'llamado' && !esTuTurno) {
            notificarTurno();
          }

          if (json?.tu_turno && json.cerca && json.estado === 'esperando') {
            avisarProximidad();
          }

          // Sin turno: NO se saca automáticamente (evita turnos fantasma de
          // gente que abre el link sin estar en el local). Se muestra el botón.
          if (json.sin_turno && inicialTurnoSolicitadoRef.current) {
            // El turno que teníamos terminó (atendido, cancelado o vencido)
            setTurnoCaducado(true);
          }

          setData(json);
        })
        .catch(err => console.error(err));
    };

    fetchData();
    // iOS: polling cada 2.5s; Desktop: cada 5s
    const pollingInterval = isIOSDevice() ? 2500 : 5000;
    const interval = setInterval(fetchData, pollingInterval);

    return () => clearInterval(interval);

  }, [cancelado, clientToken, esTuTurno, notificarTurno, avisarProximidad]);

  // 🔔 Detectar cuando la app vuelve visible (iOS background) y refrescar
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && clientToken) {
        // App vuelve al foreground
        fetch(`/api/locales/${clientToken}/estado/`, { credentials: 'include' })
          .then(r => r.json())
          .then(json => {
            if (json?.estado === 'llamado' && !esTuTurno) {
              notificarTurno();
            }
          })
          .catch(() => {});
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [clientToken, esTuTurno, notificarTurno]);
  

  // 🔥 WEBSOCKET
  useEffect(() => {
    if (!clientToken) {
      return undefined;
    }

    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(`${protocol}://${window.location.host}/ws/turnos/${clientToken}/`);

    ws.onmessage = (event) => {
      const dataWs = JSON.parse(event.data);
      const miTurno = tuTurnoRef.current;

      if (miTurno && Number(dataWs.turno) === Number(miTurno)) {
        notificarTurno();
      }
    };

    return () => ws.close();

  }, [clientToken, notificarTurno]);

  // 🔔 Mensajes del service worker (push recibido con la página abierta)
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    const handler = (event) => {
      const msg = event.data || {};
      // El push ya viene dirigido solo a esta sesión desde el backend
      if (msg.tipo === 'turno-llamado' && !esTuTurno) {
        notificarTurno();
      } else if (msg.tipo === 'turno-proximo') {
        avisarProximidad();
      }
    };

    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [esTuTurno, notificarTurno, avisarProximidad]);

  // 🎟️ Sacar turno (acción explícita del cliente, requiere un tap)
  const sacarTurno = () => {
    if (creandoTurnoRef.current || !clientToken) return;

    const esResto = data?.modo === 'resto';
    const nombreLimpio = nombreGrupo.trim();
    if (esResto && (!personasSeleccionadas || !nombreLimpio)) return;

    creandoTurnoRef.current = true;
    setTurnoCaducado(false);

    const url = esResto
      ? `/api/locales/${clientToken}/sacar_turno/?personas=${personasSeleccionadas}&nombre=${encodeURIComponent(nombreLimpio)}`
      : `/api/locales/${clientToken}/sacar_turno/`;

    fetch(url, { credentials: 'include' })
      .then(res => res.json())
      .then(turnoJson => {
        creandoTurnoRef.current = false;

        if (turnoJson.error || turnoJson.comercio_inactivo || turnoJson.cerrado) {
          setData(turnoJson);
          return;
        }

        inicialTurnoSolicitadoRef.current = true;
        tuTurnoRef.current = turnoJson.turno;

        setData({
          tu_turno: turnoJson.turno,
          turno_actual: null,
          personas_adelante: 0,
          tiempo_estimado: "0 min",
          nombre_local: turnoJson.nombre_local,
          sucursal: turnoJson.sucursal,
          vapid_public_key: turnoJson.vapid_public_key,
          cantidad_puestos: turnoJson.cantidad_puestos,
          modo: turnoJson.modo,
        });

        if (!isNotificationPermissionGranted() && typeof Notification !== 'undefined' && Notification.permission !== 'denied') {
          setShowNotificationPrompt(true);
        }
      })
      .catch(() => {
        creandoTurnoRef.current = false;
      });
  };

  // ❌ Cancelar turno
  const cancelarTurno = () => {
    fetch(`/api/locales/${clientToken}/cancelar/`, {
      method: 'POST',
      credentials: 'include'
    })
      .then(() => {
        setCancelado(true);
      });
  };

  // 🔴 Pantalla cancelado
  if (cancelado) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <h1 style={{ color: "red" }}>Turno cancelado ❌</h1>
        <p>Su turno ha sido cancelado.<br/>Puede cerrar esta pantalla.</p>
      </div>
    );
  }

  // 🎟️ Pantalla para sacar turno (acción explícita, evita turnos fantasma)
  if (data?.sin_turno) {
    const esResto = data.modo === 'resto';
    return (
      <div style={styles.container}>
        <h1 style={styles.title}>{esResto ? '🍽️ Lista de espera' : '🎟️ Mi Turno'}</h1>
        <p style={styles.localName}>{data.nombre_local}</p>
        {data.sucursal && <p style={styles.branchName}>({data.sucursal})</p>}

        {turnoCaducado && (
          <p style={styles.turnoTerminadoNota}>Tu turno anterior ya terminó.</p>
        )}

        {esResto ? (
          <>
            <p style={styles.sacarTurnoPregunta}>¿Cuántos son?</p>
            <div style={styles.personasRow}>
              {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                <button
                  key={n}
                  style={{
                    ...styles.personaButton,
                    ...(personasSeleccionadas === n ? styles.personaButtonActiva : {})
                  }}
                  onClick={() => setPersonasSeleccionadas(n)}
                >
                  {n}
                </button>
              ))}
            </div>

            {personasSeleccionadas && (
              <>
                <p style={styles.sacarTurnoPregunta}>¿A nombre de quién?</p>
                <input
                  type="text"
                  value={nombreGrupo}
                  onChange={(e) => setNombreGrupo(e.target.value)}
                  placeholder="Tu nombre"
                  maxLength={80}
                  style={styles.nombreInput}
                  autoFocus
                />
              </>
            )}

            <button
              style={{
                ...styles.sacarTurnoButton,
                ...((personasSeleccionadas && nombreGrupo.trim()) ? {} : styles.sacarTurnoButtonDisabled)
              }}
              onClick={sacarTurno}
              disabled={!personasSeleccionadas || !nombreGrupo.trim()}
            >
              📝 Anotarme en la lista
            </button>
          </>
        ) : (
          <>
            <p style={styles.sacarTurnoPregunta}>¿Estás en el local?</p>
            <button style={styles.sacarTurnoButton} onClick={sacarTurno}>
              🎟️ Sacar turno
            </button>
          </>
        )}
        <p style={styles.sacarTurnoNota}>
          Te avisamos con sonido y vibración cuando te toque.
        </p>
      </div>
    );
  }

  if (showNotificationPrompt && data?.tu_turno && !esTuTurno) {
    return (
      <div style={styles.notificationPromptOverlay}>
        <div style={styles.notificationPromptCard}>
          <h1>Permitir notificaciones</h1>
          <p style={styles.notificationPromptText}>Permitir notificaciones para avisarte cuando se acerque tu turno. No necesitas dejar la página abierta.</p>
          <button style={styles.primaryButton} onClick={askNotificationPermission}>Permitir notificaciones</button>
          <button style={styles.secondaryButton} onClick={() => setShowNotificationPrompt(false)}>No ahora</button>
        </div>
      </div>
    );
  }

  // 🟢 Pantalla ES TU TURNO
  if (esTuTurno) {
    return (
      <div style={styles.turnoScreen}>
        <style>{animacionesTurno}</style>
        <p style={styles.turnoLocal}>{data?.nombre_local}</p>
        <p style={styles.turnoSucursal}>{data?.sucursal ? `(${data.sucursal})` : ''}</p>
        <div className="mt-circulo" style={styles.turnoCirculo}>
          <span style={styles.turnoNumero}>{data?.tu_turno}</span>
        </div>
        <h1 className="mt-pulse" style={styles.turnoTitulo}>
          {data?.modo === 'resto' ? '¡TU MESA ESTÁ LISTA!' : '¡ES TU TURNO!'}
        </h1>
        <p style={styles.turnoSub}>
          {data?.modo === 'resto'
            ? 'Acercate a la entrada 👋'
            : data?.cantidad_puestos > 1 && data?.tu_puesto
              ? `Acercate a la caja ${data.tu_puesto} 👋`
              : 'Acercate al mostrador 👋'}
        </p>
      </div>
    );
  }

  if (data?.comercio_inactivo) {
    return (
      <div style={styles.blockedPage}>
        <div style={styles.blockedCard}>
          <p style={styles.localTag}>{data.nombre_local}</p>
          <p style={styles.branchTag}>({data.sucursal})</p>
          <h1 style={styles.blockedTitle}>Comercio deshabilitado</h1>
          <p>{data.mensaje}</p>
          <p>Monto: ${data.payment_amount}</p>
          <a href={data.payment_url} style={styles.paymentButton}>Ir al pago</a>
        </div>
      </div>
    );
  }

  // ⛔ Local cerrado
  if (data?.cerrado) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <h1>⛔ Local cerrado</h1>
        <p>{data.mensaje}</p>
      </div>
    );
  }

  // ⏳ Loading
  if (!data) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        Cargando...
      </div>
    );
  }

  if (data?.error) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50px' }}>
        <h1>Link invalido</h1>
        <p>{data.error}</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>

      <h1 style={styles.title}>🎟️ Mi Turno</h1>
      <p style={styles.localName}>{data.nombre_local}</p>
      <p style={styles.branchName}>({data.sucursal})</p>

      <div style={styles.card}>
        <h2>Tu turno</h2>
        <p style={styles.big}>{data.tu_turno}</p>
      </div>

      <div style={styles.card}>
        <h3>Turno actual</h3>
        <p>{data.turno_actual || "Aún no llaman"}</p>
      </div>

      <div style={styles.card}>
        <h3>{data.modo === 'resto' ? 'Grupos adelante' : 'Personas adelante'}</h3>
        <p>{data.personas_adelante}</p>
      </div>

      <div style={styles.card}>
        <h3>Tiempo estimado</h3>
        <p>{data.tiempo_estimado}</p>
      </div>

      {data.cerca && (
        <div style={styles.cercaCard}>
          🔔 ¡Tu turno se acerca!{' '}
          {data.personas_adelante > 0
            ? `Faltan ${data.personas_adelante + 1} llamados o menos.`
            : 'Sos el próximo.'}{' '}
          Acercate al local.
        </div>
      )}

      <button style={styles.button} onClick={cancelarTurno}>
        Cancelar turno
      </button>

      {mostrarBannerIOS && (
        <div style={styles.iosBanner}>
          <p style={styles.iosBannerTitle}>
            📲 Activá el aviso con sonido y vibración
          </p>
          <p style={styles.iosBannerText}>
            En iPhone, para que el teléfono te avise aunque cierres el navegador:
          </p>
          <ol style={styles.iosBannerSteps}>
            <li style={styles.iosBannerStep}>
              Tocá el botón <strong>Compartir</strong> <IconoCompartir />
              {getIOSBrowser() === 'chrome'
                ? ' (arriba a la derecha)'
                : ' (en la barra de abajo)'}
            </li>
            <li style={styles.iosBannerStep}>
              Elegí <strong>Agregar a pantalla de inicio</strong> ➕
            </li>
            <li style={styles.iosBannerStep}>
              Abrí <strong>MiTurno</strong> desde el ícono nuevo y sacá tu turno ahí
            </li>
          </ol>
          <button style={styles.iosBannerClose} onClick={() => setIosBannerDismissed(true)}>
            Entendido
          </button>
        </div>
      )}

    </div>
  );
}

// Animaciones de la pantalla "ES TU TURNO" (los estilos inline no soportan keyframes)
const animacionesTurno = `
@keyframes mtPop {
  0% { transform: scale(0.3); opacity: 0; }
  70% { transform: scale(1.08); }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes mtPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.06); }
}
@keyframes mtGlow {
  0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.45); }
  100% { box-shadow: 0 0 0 32px rgba(255, 255, 255, 0); }
}
.mt-circulo { animation: mtPop 0.5s ease-out both, mtGlow 1.8s ease-out 0.6s infinite; }
.mt-pulse { animation: mtPulse 1.6s ease-in-out infinite; }
`;

const styles = {
  container: {
    textAlign: 'center',
    padding: '20px',
    fontFamily: 'Arial'
  },
  title: {
    marginBottom: '8px'
  },
  localName: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 'bold'
  },
  branchName: {
    marginTop: '6px',
    marginBottom: '24px',
    color: '#5b5b5b'
  },
  card: {
    background: '#f5f5f5',
    padding: '15px',
    margin: '10px',
    borderRadius: '10px'
  },
  big: {
    fontSize: '40px',
    fontWeight: 'bold'
  },
  button: {
    marginTop: '20px',
    padding: '10px 20px',
    background: 'red',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px'
  },
  turnoScreen: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '24px',
    textAlign: 'center',
    color: '#fff',
    background: 'linear-gradient(160deg, #047857 0%, #10b981 55%, #065f46 100%)',
    fontFamily: 'Arial'
  },
  turnoLocal: {
    margin: 0,
    fontSize: '22px',
    fontWeight: 'bold',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    opacity: 0.95
  },
  turnoSucursal: {
    marginTop: '4px',
    marginBottom: '40px',
    fontSize: '15px',
    opacity: 0.8
  },
  turnoCirculo: {
    width: '220px',
    height: '220px',
    borderRadius: '50%',
    background: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 24px 60px rgba(0,0,0,0.25)'
  },
  turnoNumero: {
    fontSize: '100px',
    fontWeight: 'bold',
    color: '#047857',
    lineHeight: 1
  },
  turnoTitulo: {
    marginTop: '36px',
    marginBottom: '8px',
    fontSize: '34px',
    letterSpacing: '0.04em'
  },
  turnoSub: {
    margin: 0,
    fontSize: '18px',
    opacity: 0.9
  },
  sacarTurnoPregunta: {
    marginTop: '40px',
    fontSize: '22px',
    fontWeight: 'bold'
  },
  sacarTurnoButton: {
    marginTop: '8px',
    width: '100%',
    maxWidth: '380px',
    padding: '24px 32px',
    fontSize: '26px',
    fontWeight: 'bold',
    background: '#0f7b6c',
    color: '#fff',
    border: 'none',
    borderRadius: '18px',
    cursor: 'pointer',
    boxShadow: '0 10px 28px rgba(15, 123, 108, 0.35)'
  },
  sacarTurnoButtonDisabled: {
    opacity: 0.45,
    cursor: 'default',
    boxShadow: 'none'
  },
  personasRow: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '10px',
    margin: '8px auto 18px',
    maxWidth: '380px'
  },
  personaButton: {
    width: '58px',
    height: '58px',
    fontSize: '22px',
    fontWeight: 'bold',
    background: '#fff',
    color: '#0f7b6c',
    border: '2px solid #0f7b6c',
    borderRadius: '14px',
    cursor: 'pointer'
  },
  personaButtonActiva: {
    background: '#0f7b6c',
    color: '#fff'
  },
  nombreInput: {
    width: '100%',
    maxWidth: '380px',
    padding: '16px',
    fontSize: '18px',
    textAlign: 'center',
    border: '2px solid #0f7b6c',
    borderRadius: '14px',
    margin: '0 auto 18px',
    boxSizing: 'border-box'
  },
  sacarTurnoNota: {
    marginTop: '16px',
    fontSize: '14px',
    color: '#5b5b5b'
  },
  turnoTerminadoNota: {
    display: 'inline-block',
    padding: '10px 16px',
    borderRadius: '10px',
    background: '#f3f4f6',
    color: '#444',
    fontSize: '15px'
  },
  cercaCard: {
    background: '#fff7e6',
    border: '2px solid #f5a623',
    color: '#8a5a00',
    padding: '14px',
    margin: '10px',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: 'bold'
  },
  localTag: {
    margin: 0,
    fontSize: '20px',
    letterSpacing: '0.08em',
    textTransform: 'uppercase'
  },
  branchTag: {
    marginTop: '6px',
    marginBottom: '26px',
    fontSize: '16px'
  },
  blockedPage: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    background: '#f4efe7'
  },
  blockedCard: {
    maxWidth: '520px',
    width: '100%',
    background: '#ffffff',
    borderRadius: '24px',
    padding: '32px',
    textAlign: 'center',
    boxShadow: '0 18px 48px rgba(0, 0, 0, 0.12)'
  },
  blockedTitle: {
    marginTop: '10px'
  },
  paymentButton: {
    display: 'inline-block',
    marginTop: '18px',
    padding: '14px 28px',
    borderRadius: '999px',
    background: '#0f7b6c',
    color: '#fff',
    textDecoration: 'none'
  },
  notificationPromptOverlay: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
    background: 'rgba(0, 0, 0, 0.6)'
  },
  notificationPromptCard: {
    width: '100%',
    maxWidth: '520px',
    padding: '32px',
    borderRadius: '20px',
    background: '#ffffff',
    textAlign: 'center',
    boxShadow: '0 24px 60px rgba(0,0,0,0.18)'
  },
  notificationPromptText: {
    marginBottom: '24px',
    fontSize: '18px',
    color: '#333'
  },
  primaryButton: {
    width: '100%',
    padding: '14px 16px',
    borderRadius: '12px',
    border: 'none',
    background: '#0f7b6c',
    color: '#fff',
    fontSize: '16px',
    cursor: 'pointer',
    marginBottom: '12px'
  },
  iosBanner: {
    position: 'fixed',
    left: '12px',
    right: '12px',
    bottom: '12px',
    padding: '14px 16px',
    borderRadius: '14px',
    background: '#1c1c1e',
    color: '#fff',
    textAlign: 'left',
    boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
    zIndex: 1000
  },
  iosBannerTitle: {
    margin: '0 0 8px 0',
    fontSize: '16px',
    fontWeight: 'bold'
  },
  iosBannerText: {
    margin: '0 0 10px 0',
    fontSize: '14px',
    lineHeight: 1.4
  },
  iosBannerSteps: {
    margin: '0 0 12px 0',
    paddingLeft: '20px'
  },
  iosBannerStep: {
    fontSize: '14px',
    lineHeight: 1.5,
    marginBottom: '6px'
  },
  iosBannerClose: {
    padding: '8px 14px',
    borderRadius: '8px',
    border: 'none',
    background: '#0f7b6c',
    color: '#fff',
    fontSize: '14px',
    cursor: 'pointer'
  },
  secondaryButton: {
    width: '100%',
    padding: '14px 16px',
    borderRadius: '12px',
    border: '1px solid #ccc',
    background: '#fff',
    color: '#333',
    fontSize: '16px',
    cursor: 'pointer'
  }
};

export default App;
